import React from 'react';
import { ItemSelection } from 'src/types';
import Checkbox from '../Checkbox/Checkbox';
import OptionBtn from './OptionBtn';
import './SelectableGrid.scss';
import { CustomListInterface, SelectedItemProps } from './types';

// Mutators
export const List: CustomListInterface<ItemSelection> = {
  add(list, item) {
    return [...list, item];
  },
  remove(list, item) {
    return list?.filter((otherItem) => item?.label !== otherItem?.label) ?? [];
  },
  has(list, item) {
    return list?.some((otherItem) => item?.label === otherItem?.label);
  },
};

export interface ButtonProps {
  btnSize?: number;
  disabled?: boolean;
  phoneColNum: number;
  idPrefix(s: string): string;
}

export interface SelectableGridProps extends SelectedItemProps {
  allItems: ItemSelection[];
  buttonProps: ButtonProps;
  id: string;
  limitText?: string;
  maxSelectCount?: number;
  optionName?: string;
  showSelectAllOption?: boolean;
  title: string;
}

export const SelectableGrid: React.FC<SelectableGridProps> = ({
  allItems = [],
  buttonProps: { phoneColNum, btnSize = 35, idPrefix = (id) => `button_${id}` },
  children,
  handleSelect,
  id,
  limitText,
  maxSelectCount: maxLimit,
  optionName = '',
  selectedItems = [],
  showSelectAllOption = true,
  title,
}) => {
  /* Predicate functions closed over 'selectedItems' */
  // an item is seleted if it's in the list of selections
  const isSelected = (item: ItemSelection) => List.has(selectedItems, item);

  // all Options box is checked if all options are checked and vice-versa
  const isSelectAllChecked =
    showSelectAllOption && allItems.length === selectedItems?.length;

  // disable anything not yet selected if we've reached or exceeded our maxLimit
  const allOptionsDisabled = maxLimit && selectedItems?.length >= maxLimit;

  /* onChange/Click events */
  // Single button click: dispatch a different event if we're selected or not.
  const addOrRemoveItem = (item: ItemSelection) => {
    const f = isSelected(item) ? List.remove : List.add;
    return f(selectedItems, item);
  };

  // Toggle SelectAll
  const selectAllorNone = () => (isSelectAllChecked ? [] : allItems);

  return (
    <div className="selectable-grid">
      <div className="selectable-grid-title">{title}</div>
      <div className="selectable-grid-limit-text">{limitText}</div>
      <div className="selectable-grid-options-container">
        {allItems.map((item, i) => {
          return (
            <OptionBtn
              key={i}
              disabled={!!allOptionsDisabled}
              id={`${id}-${idPrefix(String(i))}`}
              phoneColNum={phoneColNum}
              selected={isSelected(item)}
              size={btnSize + 'px'}
              onClick={() => {
                handleSelect(addOrRemoveItem(item));
              }}
            >
              {item.label}
            </OptionBtn>
          );
        })}
        {limitText && (
          <div className="selectable-grid-phone-limit-text">{limitText}</div>
        )}
      </div>
      {showSelectAllOption && (
        <div className="selectable-grid-select-all-container">
          <div className="selectable-grid-select-all">
            <Checkbox
              ariaLabel={`Toggle All Options`}
              checked={isSelectAllChecked}
              contextStyle={{ marginTop: '5px' }}
              disabled={false}
              e2eSuffix={`select-all-options-${id}`}
              id={`checkbox-all-options-${id}`}
              inline={true}
              tabIndex={'0'}
              onClick={() => {
                handleSelect(selectAllorNone(), isSelectAllChecked);
              }}
            />
            <label
              className="selectable-grid-select-all-option"
              htmlFor={`checkbox-all-options-${id}`}
            >
              Select All {optionName}
            </label>
          </div>
          <div className="selectable-grid-select-all-phone">
            <OptionBtn
              id="select-all-option-btn"
              selected={isSelectAllChecked}
              size={'100%'}
              onClick={() => {
                handleSelect(selectAllorNone());
              }}
            >
              {isSelectAllChecked
                ? 'Deselect All ' + optionName
                : 'Select All ' + optionName}
            </OptionBtn>
          </div>
        </div>
      )}
      <footer className="selectable-grid-footer-container">{children}</footer>
    </div>
  );
};
